import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Text, Button as ThemeUiButton, useThemeUI } from "theme-ui";
import flow from "lodash/fp/flow";
import isEmpty from "lodash/fp/isEmpty";
import map from "lodash/fp/map";
import remove from "lodash/fp/remove";
import pick from "lodash/fp/pick";
import some from "lodash/fp/some";
import toPairs from "lodash/fp/toPairs";
import values from "lodash/fp/values";

import { useTranslation } from "hooks";
import { updateUserChoiceValues } from "store/app/appSlice";
import { selectUserChoiceValues } from "store/app";
import Checkbox from "../Checkbox";
import SkateCustomiseBladeModal from "./SkateCustomiseBladeModal";
import getFieldNames from "./getFieldNames";
import * as styles from "./SkateCustomiseBlade.styles";

export default function SkateCustomiseBlade({
  name,
  outerTitle,
  title,
  bladeImage,
  bladeImageForPersonalisation,
  fields,
  customiseFeeNote,
  cancelButtonLabel,
  submitButtonLabel,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const { t } = useTranslation();

  const ref = useRef();

  const dispatch = useDispatch();

  const [renderCustomiseModal, setRenderCustomiseModal] = useState(false);

  const userChoiceValues = useSelector(selectUserChoiceValues);

  const fieldNames = getFieldNames(fields);

  const hasBladeCustomisation = flow(
    // Remove these two items from check as they are always present in userChoiceValues when reloading
    remove((name) =>
      ["customizeBladeNoPersonalization", "Additional Blades"].includes(name)
    ),
    (fieldNames) => pick(fieldNames)(userChoiceValues),
    values,
    some((a) => !isEmpty(a))
  )(fieldNames);

  const dispatchUpdateUserChoiceValues = (fieldValues) => {
    dispatch(
      updateUserChoiceValues(
        flow(
          toPairs,
          map(([name, value]) => ({ name, value }))
        )(fieldValues)
      )
    );
  };

  const clearAllValues = () => {
    dispatchUpdateUserChoiceValues(
      fieldNames.reduce((acc, fieldName) => ({ ...acc, [fieldName]: null }), {})
    );
  };

  useEffect(() => {
    ref.current.closest(
      `.${process.env.REACT_APP_CONTAINER_CSS_CLASSNAME}`
    ).style.position = "relative";
  }, []);

  useEffect(() => {
    dispatchUpdateUserChoiceValues({ [name]: hasBladeCustomisation });
  }, [hasBladeCustomisation]);

  return (
    <Box ref={ref} sx={styles.container}>
      <Checkbox
        size={isMobile ? "small" : "medium"}
        value={hasBladeCustomisation}
        onChange={(checkboxValue) => {
          if (checkboxValue) {
            setRenderCustomiseModal(true);
          } else {
            clearAllValues();
          }
        }}
      >
        {t("Customized?")}
      </Checkbox>

      {hasBladeCustomisation && (
        <Text variant="h4Light">
          <Box
            as="span"
            dangerouslySetInnerHTML={{ __html: "&nbsp;&nbsp;|&nbsp;&nbsp;" }}
          />
          <ThemeUiButton
            variant="unstyled"
            sx={{
              fontFamily: "inherit",
              fontWeight: "inherit",
              color: "primary",
            }}
            onClick={() => setRenderCustomiseModal(true)}
          >
            {t("Review Customizations")}
          </ThemeUiButton>
        </Text>
      )}

      {renderCustomiseModal &&
        createPortal(
          <SkateCustomiseBladeModal
            outerTitle={outerTitle}
            title={title}
            bladeImage={bladeImage}
            customiseFeeNote={customiseFeeNote}
            bladeImageForPersonalisation={bladeImageForPersonalisation}
            cancelButtonLabel={cancelButtonLabel}
            submitButtonLabel={
              hasBladeCustomisation ? t("APPLY CHANGES") : submitButtonLabel
            }
            fields={fields}
            onClose={() => setRenderCustomiseModal(false)}
          />,
          ref.current.closest(
            `.${process.env.REACT_APP_CONTAINER_CSS_CLASSNAME}`
          )
        )}
    </Box>
  );
}
